import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { Box, Loader, ScrollContainer, useDevice } from "@memorang/ui";
import { Redirect, useRouter } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LearningPathHeader } from "../components/LearningPathHeader";
import {
	type ModuleStatusFilter,
	ModuleStatusFilterButtons,
} from "../components/ModuleStatusFilterButtons";
import {
	type ModuleViewMode,
	ModuleViewModeToggle,
} from "../components/ModuleViewModeToggle";
import { ModulesList } from "../components/ModulesList";
import { PurchaseDialog } from "../components/PurchaseDialog";
import { getProethicaOrderedEntries } from "../helpers/proethica-module-list";
import { buildProethicaProgress } from "../helpers/proethica-progress-data";
import { useProethicaModuleList } from "../hooks/useProethicaModuleList";
import { useProethicaTestScores } from "../hooks/useProethicaTestScores";

const MODULES_CONTENT_MAX_WIDTH = 1120;

const ModulesContainer = () => {
	const router = useRouter();
	const demoExamId = useDemoExamStore((state) => state.currentExam.id);
	const { data: mixedItemDist, isLoading } = useMixedItemDist();
	const {
		getModuleListItemProps,
		startModule,
		resolveModuleItems,
		hasAccess,
		preTestCompleted,
		postTestPassed,
	} = useProethicaModuleList();
	const { testScores } = useProethicaTestScores(mixedItemDist?.exams, {
		preTestCompleted,
		postTestPassed,
	});
	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
	const [activeFilter, setActiveFilter] = useState<ModuleStatusFilter>("all");
	const [viewMode, setViewMode] = useState<ModuleViewMode>("list");
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { t } = useTranslation();

	if (demoExamId !== "proethica") {
		return <Redirect href="/home" />;
	}

	if (isLoading || !mixedItemDist) {
		return <Loader />;
	}

	const items = resolveModuleItems(
		getProethicaOrderedEntries(mixedItemDist.exams),
	);

	const moduleProps = items.map((tag) => getModuleListItemProps(tag, t));
	const curriculumModuleProps = moduleProps.filter((module) => !module.isTest);
	const moduleDoneCount = curriculumModuleProps.filter(
		(module) => module.status === "COMPLETE",
	).length;
	const moduleTotalCount = curriculumModuleProps.length;
	const { scoreRows } = buildProethicaProgress(
		moduleProps,
		mixedItemDist.tags ?? [],
		{ preTestCompleted, postTestPassed },
	);
	const scoreRowsWithTests = scoreRows.map((row) =>
		row.isTest && testScores.has(row.id)
			? { ...row, scorePercent: testScores.get(row.id)! }
			: row,
	);

	const handleShowPurchase = () => {
		if (isWeb) {
			setShowPurchaseDialog(true);
			return;
		}
		router.push({
			pathname: "/purchase",
			params: {
				productType: "STUDY_PACK",
			},
		});
	};

	const filteredItems = items.filter((tag) => {
		if (activeFilter === "all") {
			return true;
		}
		return getModuleListItemProps(tag, t).status === activeFilter;
	});

	return (
		<>
			<ScrollContainer
				style={{
					backgroundColor: theme.colors.background,
				}}
				contentContainerStyle={{
					paddingTop: isMobile ? 16 : 32,
					paddingBottom: 48,
				}}
			>
				<Box
					width="100%"
					maxWidth={MODULES_CONTENT_MAX_WIDTH}
					alignSelf="center"
					paddingHorizontal={isMobile ? 16 : 32}
					style={{ gap: 16 }}
				>
					<Box
						flexDirection={isMobile ? "column" : "row"}
						justifyContent="space-between"
						alignItems={isMobile ? "stretch" : "center"}
						width="100%"
						style={{ gap: 16 }}
					>
						<Box width={isMobile ? "100%" : 560} maxWidth="100%" minWidth={0}>
							<ModuleStatusFilterButtons
								value={activeFilter}
								onValueChange={setActiveFilter}
								hasAccess={hasAccess}
							/>
						</Box>
						<ModuleViewModeToggle
							value={viewMode}
							onValueChange={setViewMode}
						/>
					</Box>
					{activeFilter === "all" ? (
						<LearningPathHeader
							doneCount={moduleDoneCount}
							totalCount={moduleTotalCount}
						/>
					) : null}
					<ModulesList
						items={filteredItems}
						scoreRows={scoreRowsWithTests}
						loading={isLoading}
						handleSelectTopic={startModule}
						handleShowPurchase={handleShowPurchase}
						getModuleListItemProps={getModuleListItemProps}
						viewMode={isMobile ? "grid" : viewMode}
					/>
				</Box>
			</ScrollContainer>
			{showPurchaseDialog && (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={() => setShowPurchaseDialog(false)}
					productType="STUDY_PACK"
				/>
			)}
		</>
	);
};

export default ModulesContainer;
